import React, { useState, useEffect } from 'react';
import { walletAPI } from '../../services/api';
import './TransactionForm.css';

function TransactionForm({ 
  type = 'deposit', // 'deposit', 'withdrawal', 'transfer'
  walletId = null,
  availableWallets = [],
  onSubmit = () => {},
  onCancel = () => {},
  loading = false,
  disabled = false
}) {
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    fromWalletId: walletId || '',
    toWalletId: walletId || '',
    metadata: {}
  });
  
  const [errors, setErrors] = useState({});
  const [validation, setValidation] = useState({ isValid: false, errors: [] });
  const [isValidating, setIsValidating] = useState(false);

  // Set default wallet based on type
  useEffect(() => {
    if (type === 'deposit' || type === 'withdrawal') {
      setFormData(prev => ({
        ...prev,
        fromWalletId: type === 'withdrawal' ? walletId : '',
        toWalletId: type === 'deposit' ? walletId : ''
      }));
    }
  }, [type, walletId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const validateForm = async () => {
    const newErrors = {};
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (type === 'transfer') {
      if (!formData.fromWalletId) {
        newErrors.fromWalletId = 'Source wallet is required';
      }
      if (!formData.toWalletId) {
        newErrors.toWalletId = 'Destination wallet is required';
      }
      if (formData.fromWalletId === formData.toWalletId) {
        newErrors.toWalletId = 'Cannot transfer to the same wallet';
      }
    }
    
    setErrors(newErrors);
    
    // If basic validation passes, validate with backend
    if (Object.keys(newErrors).length === 0 && formData.amount && formData.description) {
      setIsValidating(true);
      try {
        const transactionData = {
          type,
          amount: parseFloat(formData.amount),
          fromWalletId: type === 'withdrawal' || type === 'transfer' ? formData.fromWalletId : null,
          toWalletId: type === 'deposit' || type === 'transfer' ? formData.toWalletId : null
        };
        
        const validationResult = await walletAPI.validateTransaction(transactionData);
        const validationData = validationResult.data?.data?.validation || validationResult.data?.validation || {};
        setValidation({
          isValid: validationData.isValid || false,
          errors: validationData.errors || []
        });
      } catch (error) {
        setValidation({ isValid: false, errors: ['Failed to validate transaction'] });
      } finally {
        setIsValidating(false);
      }
    } else {
      setValidation({ isValid: false, errors: [] });
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid || !validation.isValid) {
      return;
    }
    
    const transactionData = {
      amount: parseFloat(formData.amount),
      description: formData.description.trim(),
      metadata: formData.metadata
    };
    
    if (type === 'transfer') {
      transactionData.fromWalletId = formData.fromWalletId;
      transactionData.toWalletId = formData.toWalletId;
    }
    
    onSubmit(transactionData);
  };

  const getFormTitle = () => {
    switch (type) {
      case 'deposit':
        return 'Make Deposit';
      case 'withdrawal':
        return 'Make Withdrawal';
      case 'transfer':
        return 'Transfer Funds';
      default:
        return 'Transaction';
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return 'Processing...';
    switch (type) {
      case 'deposit':
        return 'Deposit Funds';
      case 'withdrawal':
        return 'Withdraw Funds';
      case 'transfer':
        return 'Transfer Funds';
      default:
        return 'Submit';
    }
  };

  // Debounce validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.amount && formData.description) {
        validateForm();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.amount, formData.description, formData.fromWalletId, formData.toWalletId]);

  return (
    <div className="transaction-form">
      <div className="form-header">
        <h2>{getFormTitle()}</h2>
        <div className={`transaction-type-badge type-${type}`}>
          {type.toUpperCase()}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {type === 'transfer' && (
          <>
            <div className="form-group">
              <label htmlFor="fromWallet">From Wallet *</label>
              <select
                id="fromWallet"
                value={formData.fromWalletId}
                onChange={(e) => handleInputChange('fromWalletId', e.target.value)}
                className={errors.fromWalletId ? 'error' : ''}
                disabled={disabled}
              >
                <option value="">Select source wallet</option>
                {(availableWallets || []).map(wallet => (
                  <option key={wallet._id} value={wallet._id}>
                    {wallet.ownerName} ({formatCurrency(wallet.balance)})
                  </option>
                ))}
              </select>
              {errors.fromWalletId && (
                <span className="error-message">{errors.fromWalletId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="toWallet">To Wallet *</label>
              <select
                id="toWallet"
                value={formData.toWalletId}
                onChange={(e) => handleInputChange('toWalletId', e.target.value)}
                className={errors.toWalletId ? 'error' : ''}
                disabled={disabled}
              >
                <option value="">Select destination wallet</option>
                {(availableWallets || []).map(wallet => (
                  <option key={wallet._id} value={wallet._id}>
                    {wallet.ownerName} ({formatCurrency(wallet.balance)})
                  </option>
                ))}
              </select>
              {errors.toWalletId && (
                <span className="error-message">{errors.toWalletId}</span>
              )}
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="amount">Amount *</label>
          <div className="amount-input-container">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="amount"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              className={errors.amount ? 'error' : ''}
              disabled={disabled}
            />
          </div>
          {errors.amount && (
            <span className="error-message">{errors.amount}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter transaction description..."
            rows="3"
            className={errors.description ? 'error' : ''}
            disabled={disabled}
          />
          {errors.description && (
            <span className="error-message">{errors.description}</span>
          )}
        </div>

        {/* Validation Status */}
        {isValidating && (
          <div className="validation-status validating">
            <div className="validation-spinner"></div>
            <span>Validating transaction...</span>
          </div>
        )}

        {!isValidating && validation.errors && validation.errors.length > 0 && (
          <div className="validation-status invalid">
            <div className="validation-errors">
              {(validation.errors || []).map((error, index) => (
                <div key={index} className="validation-error">
                  ⚠️ {error}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isValidating && validation.isValid && formData.amount && formData.description && (
          <div className="validation-status valid">
            ✅ Transaction is valid and ready to process
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={disabled || loading || !validation.isValid || isValidating}
          >
            {getSubmitButtonText()}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TransactionForm;